/**
 * Centralized router paths that match the routes defined in router.tsx
 * Use these helper functions to ensure type-safe navigation
 */

export const routerPaths = {
  // Auth routes
  login: () => '/login',
  register: () => '/register',
  confirmationCode: () => '/confirmation-code',
  onboarding: () => '/onboarding',
  maintenanceOnboarding: () => '/maintenance-onboarding',
  pendingInvitations: (token?: string) => token ? `/invitations/${token}` : '/invitations',

  // Main routes
  dashboard: () => '/',
  profile: () => '/profile',

  // Loads
  loads: () => '/loads',
  loadDetail: (id: string) => `/loads/${id}`,
  loadDetailLegacy: (id: string) => `/loads/${id}/legacy`,
  loadEdit: (id: string) => `/loads/${id}/edit`,
  loadNew: () => '/loads/new',
  loadsRecurring: () => '/loads/recurring',
  loadRecurringNew: () => '/loads/recurring/new',
  loadRecurringEdit: (id: string) => `/loads/recurring/${id}/edit`,
  loadsTemplates: () => '/loads/templates',
  loadTemplateNew: () => '/loads/templates/new',
  loadTemplateEdit: (id: string) => `/loads/templates/${id}/edit`,
  loadCalculator: () => '/loads/calculator',

  // Carriers
  carriers: () => '/carriers',
  carrierDetail: (id: string) => `/carriers/${id}`,
  carrierNew: () => '/carriers/new',
  carrierEdit: (id: string) => `/carriers/${id}/edit`,

  // Customers
  customers: () => '/customers',
  customerDetail: (id: string) => `/customers/${id}`,

  // Quotes
  quotes: () => '/quotes',
  quoteDetail: (id: string) => `/quotes/${id}`,
  quoteNew: () => '/quotes/new',
  quoteEdit: (id: string) => `/quotes/${id}/edit`,

  // Drivers
  drivers: () => '/drivers',
  driverDetail: (id: string) => `/drivers/${id}`,

  // Fleet
  trucks: () => '/trucks',
  trailers: () => '/trailers',
  truckDetail: (id: string) => `/fleet/trucks/${id}`,
  trailerDetail: (id: string) => `/fleet/trailers/${id}`,

  // Invoices
  invoices: () => '/invoices',
  invoiceDetail: (id: string) => `/invoices/${id}`,

  // Factoring
  factoring: () => '/factoring',
  factoringDetail: (id: string) => `/factoring/${id}`,

  // Settlements
  settlements: () => '/settlements',
  settlementDetail: (id: string) => `/settlements/${id}`,

  // Paystubs
  paystubs: () => '/paystubs',
  paystubDetail: (id: string) => `/paystubs/${id}`,

  // Expenses
  expenses: () => '/expenses',
  expenseNew: () => '/expenses/new',
  expenseDetail: (id: string) => `/expenses/${id}`,
  expenseEdit: (id: string) => `/expenses/${id}/edit`,

  // Fuel
  fuel: () => '/fuel',
  fuelNew: () => '/fuel/new',
  fuelDetail: (id: string) => `/fuel/${id}`,

  // Maintenance
  maintenance: () => '/maintenance',
  maintenanceDetail: (id: string) => `/maintenance/${id}`,
  maintenanceWorkOrders: () => '/maintenance/work-orders',
  workOrderDetail: (id: string) => `/maintenance/work-orders/${id}`,
  workOrderEdit: (id: string) => `/maintenance/work-orders/${id}/edit`,
  maintenanceFleet: () => '/maintenance/fleet',
  maintenanceFleetDetail: (id: string) => `/maintenance/fleet/${id}`,
  fleetMaintenanceDetail: (id: string) => `/maintenance/fleet/${id}`,
  parts: () => '/maintenance/parts',
  partDetail: (id: string) => `/maintenance/parts/${id}`,
  technicians: () => '/maintenance/technicians',
  technicianDetail: (id: string) => `/maintenance/technicians/${id}`,
  maintenanceInspections: () => '/maintenance/inspections',
  maintenanceInspectionDetail: (id: string) => `/maintenance/inspections/${id}`,
  yards: () => '/yards',
  yardDetail: (id: string) => `/yards/${id}`,
  yardNew: () => '/yards/new',
  yardSpotDetail: (yardId: string, spotId: string) => `/yards/${yardId}/spots/${spotId}`,
  yardEventDetail: (yardId: string, eventId: string) => `/yards/${yardId}/events/${eventId}`,
  yardCreateEvent: (yardId?: string) => yardId ? `/yards/${yardId}/events/new` : '/yards/events/new',
  yardFleetOverview: () => '/yards/fleet-overview',
  yardFormTemplates: () => '/yards/form-templates',

  // Inspections
  inspections: () => '/inspections',
  inspectionNew: () => '/inspections/new',
  inspectionDetail: (id: string) => `/inspections/${id}`,

  // Compliance
  compliance: () => '/compliance',
  complianceDetail: (id: string) => `/compliance/${id}`,

  // Integrations
  integrations: () => '/integrations',
  quickbooks: () => '/integrations/quickbooks',
  quickbooksCallback: () => '/integrations/quickbooks/callback',
  relay: () => '/integrations/relay',
  fleetrock: () => '/integrations/fleetrock',
  relayConfig: () => '/integrations/relay/configure',
  samsaraCallback: () => '/samsara',

  // Tracking
  tracking: () => '/tracking',
  trackingView: (id: string) => `/tracking/${id}`,

  // Settings
  settings: () => '/settings',
  subscriptionPlans: () => '/settings/subscription/plans',
  myInvitations: () => '/my-invitations',

  // Other routes
  analytics: () => '/analytics',
  auditLog: () => '/audit-log',
  dispatch: () => '/dispatch',
  documents: () => '/documents',
  edi: () => '/edi',
  help: () => '/help',
  hos: () => '/hos',
  ifta: () => '/ifta',
  invitationsAccept: (token: string) => `/invitations/${token}`,
  lanes: () => '/lanes',
  margins: () => '/margins',
  marketplace: () => '/marketplace',
  messaging: () => '/messaging',
  portal: () => '/portal',
  portalDemo: () => '/portal-demo',
  rates: () => '/rates',
  reports: () => '/reports',
  agingReport: () => '/reports/aging',

  // Stripe
  stripeSuccess: () => '/stripe/success',
  stripeFailure: () => '/stripe/failure',
  stripCancel: () => '/stripe/cancel',
} as const;

// Type-safe route builder for routes with query params (legacy support)
export const buildQueryRoute = (basePath: string, params: Record<string, string>) => {
  const queryString = new URLSearchParams(params).toString();
  return `${basePath}?${queryString}`;
};
